import api from './api';
import type { AxiosResponse } from 'axios';

// Types for Quiz Management
export interface Quiz {
  id: number;
  title: string;
  description: string;
  subject: string;
  course_id: number;
  course_name: string;
  class_id: number;
  class_name: string;
  teacher_id: number;
  teacher_name: string;
  quiz_type: 'multiple_choice' | 'true_false' | 'short_answer' | 'essay' | 'mixed' | 'google_form';
  duration: number; // in minutes
  total_questions: number;
  total_points: number;
  passing_score: number;
  start_date: string;
  end_date: string;
  status: 'draft' | 'published' | 'active' | 'completed' | 'archived';
  is_timed: boolean;
  allow_retake: boolean;
  max_attempts: number;
  shuffle_questions: boolean;
  show_results: boolean;
  google_form_id?: string;
  google_form_url?: string;
  created_at: string;
  updated_at: string;
  // Per-level question counts, sent on list responses only.
  difficulty_breakdown?: QuizDifficultyBreakdown;
}

export type QuizDifficulty = 'easy' | 'medium' | 'hard';

export interface QuizDifficultyBreakdown {
  easy: number;
  medium: number;
  hard: number;
  unset: number;
}

export interface QuizQuestion {
  id: number;
  quiz_id: number;
  question_text: string;
  question_type: 'multiple_choice' | 'true_false' | 'short_answer' | 'essay';
  points: number;
  order: number;
  is_required: boolean;
  options?: QuizQuestionOption[];
  correct_answer?: string;
  explanation?: string;
  difficulty?: QuizDifficulty;
}

export interface QuizQuestionOption {
  id: number;
  question_id: number;
  option_text: string;
  is_correct: boolean;
  order: number;
}

export interface QuizAttempt {
  id: number;
  quiz_id: number;
  student_id: number;
  student_name: string;
  start_time: string;
  end_time?: string;
  score?: number;
  max_score: number;
  percentage?: number;
  status: 'in_progress' | 'completed' | 'abandoned';
  answers: QuizAnswer[];
  time_taken?: number; // in minutes
  attempt_number: number;
}

export interface QuizAnswer {
  id: number;
  attempt_id: number;
  question_id: number;
  answer_text: string;
  selected_options?: number[];
  is_correct?: boolean;
  points_earned?: number;
  feedback?: string;
}

export interface CreateQuizRequest {
  title: string;
  description: string;
  course_id: number;
  class_id: number;
  quiz_type: 'multiple_choice' | 'true_false' | 'short_answer' | 'essay' | 'mixed' | 'google_form';
  duration: number;
  total_points: number;
  passing_score: number;
  start_date: string;
  end_date: string;
  is_timed: boolean;
  allow_retake: boolean;
  max_attempts: number;
  shuffle_questions: boolean;
  show_results: boolean;
  google_form_url?: string;
  questions: CreateQuizQuestionRequest[];
}

export interface CreateQuizQuestionRequest {
  question_text: string;
  question_type: 'multiple_choice' | 'true_false' | 'short_answer' | 'essay';
  points: number;
  order: number;
  is_required: boolean;
  options?: CreateQuizQuestionOptionRequest[];
  correct_answer?: string;
  explanation?: string;
}

export interface CreateQuizQuestionOptionRequest {
  option_text: string;
  is_correct: boolean;
  order: number;
}

export interface UpdateQuizRequest {
  title?: string;
  description?: string;
  duration?: number;
  total_points?: number;
  passing_score?: number;
  start_date?: string;
  end_date?: string;
  status?: 'draft' | 'published' | 'active' | 'completed' | 'archived';
  is_timed?: boolean;
  allow_retake?: boolean;
  max_attempts?: number;
  shuffle_questions?: boolean;
  show_results?: boolean;
}

export interface QuizResponse {
  status: number;
  message: string;
  data: Quiz;
}

export interface QuizListResponse {
  status: number;
  message: string;
  data: Quiz[];
  pagination?: {
    current_page: number;
    per_page: number;
    total_pages: number;
    total_records: number;
    has_next: boolean;
    has_previous: boolean;
  };
}

export interface QuizAnalytics {
  total_quizzes: number;
  active_quizzes: number;
  completed_quizzes: number;
  average_completion_rate: number;
  average_score: number;
  total_attempts: number;
  popular_subjects: string[];
  performance_by_class: Array<{
    class_name: string;
    average_score: number;
    completion_rate: number;
  }>;
}

const quizApi = {
  /**
   * Get all quizzes for a school
   */
  async getAllQuizzes(
    page: number = 1,
    limit: number = 20,
    sort: string = 'created_at',
    order: 'asc' | 'desc' = 'desc',
    status?: string,
    course_id?: number,
    class_id?: number
  ): Promise<AxiosResponse<QuizListResponse>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      sort,
      order
    });
    
    if (status) {
      params.append('status', status);
    }
    if (course_id) {
      params.append('course_id', course_id.toString());
    }
    if (class_id) {
      params.append('class_id', class_id.toString());
    }
    
    return await api.get(`/quizzes?${params}`);
  },

  /**
   * Get quiz by ID
   */
  async getQuiz(id: number): Promise<AxiosResponse<QuizResponse>> {
    return await api.get(`/quizzes/${id}`);
  },

  /**
   * Get quiz with questions
   */
  async getQuizWithQuestions(id: number): Promise<AxiosResponse<QuizResponse & { questions: QuizQuestion[] }>> {
    return await api.get(`/quizzes/${id}/questions`);
  },

  /**
   * Create quiz
   */
  async createQuiz(data: CreateQuizRequest): Promise<AxiosResponse<QuizResponse>> {
    return await api.post('/quizzes', data);
  },

  /**
   * Update quiz
   */
  async updateQuiz(id: number, data: UpdateQuizRequest): Promise<AxiosResponse<QuizResponse>> {
    return await api.put(`/quizzes/${id}`, data);
  },

  /**
   * Delete quiz
   */
  async deleteQuiz(id: number): Promise<AxiosResponse<{ status: number; message: string }>> {
    return await api.delete(`/quizzes/${id}`);
  },

  /**
   * Publish quiz
   */
  async publishQuiz(id: number): Promise<AxiosResponse<QuizResponse>> {
    return await api.put(`/quizzes/${id}/publish`);
  },

  /**
   * Archive quiz
   */
  async archiveQuiz(id: number): Promise<AxiosResponse<QuizResponse>> {
    return await api.put(`/quizzes/${id}/archive`);
  },

  /**
   * Duplicate quiz
   */
  async duplicateQuiz(id: number): Promise<AxiosResponse<QuizResponse>> {
    return await api.post(`/quizzes/${id}/duplicate`);
  },

  /**
   * Get quiz attempts
   */
  async getQuizAttempts(quizId: number): Promise<AxiosResponse<{ data: QuizAttempt[] }>> {
    return await api.get(`/quizzes/${quizId}/attempts`);
  },

  /**
   * Get student quiz attempts
   */
  async getStudentQuizAttempts(studentId: number): Promise<AxiosResponse<{ data: QuizAttempt[] }>> {
    return await api.get(`/students/${studentId}/quiz-attempts`);
  },

  /**
   * Start quiz attempt
   */
  async startQuizAttempt(quizId: number): Promise<AxiosResponse<{ data: QuizAttempt }>> {
    return await api.post(`/quizzes/${quizId}/start-attempt`);
  },

  /**
   * Submit quiz attempt
   */
  async submitQuizAttempt(attemptId: number, answers: QuizAnswer[]): Promise<AxiosResponse<{ data: QuizAttempt }>> {
    return await api.post(`/quiz-attempts/${attemptId}/submit`, { answers });
  },

  /**
   * Get quiz results
   */
  async getQuizResults(quizId: number): Promise<AxiosResponse<{ data: QuizAttempt[] }>> {
    return await api.get(`/quizzes/${quizId}/results`);
  },

  /**
   * Grade quiz attempt
   */
  async gradeQuizAttempt(attemptId: number, grades: Array<{ question_id: number; score: number; feedback?: string }>): Promise<AxiosResponse<{ data: QuizAttempt }>> {
    return await api.post(`/quiz-attempts/${attemptId}/grade`, { grades });
  },

  /**
   * Get quiz analytics
   */
  async getQuizAnalytics(): Promise<AxiosResponse<{ data: QuizAnalytics }>> {
    return await api.get('/quizzes/analytics');
  },

  /**
   * Export quiz results
   */
  async exportQuizResults(quizId: number, format: 'csv' | 'excel' = 'csv'): Promise<AxiosResponse<Blob>> {
    return await api.get(`/quizzes/${quizId}/export-results?format=${format}`, {
      responseType: 'blob'
    });
  },

  /**
   * Get quiz templates
   */
  async getQuizTemplates(): Promise<AxiosResponse<{ data: Quiz[] }>> {
    return await api.get('/quizzes/templates');
  },

  /**
   * Create quiz from template
   */
  async createQuizFromTemplate(templateId: number, data: Partial<CreateQuizRequest>): Promise<AxiosResponse<QuizResponse>> {
    return await api.post(`/quizzes/templates/${templateId}/create`, data);
  }
};

export default quizApi; 